// ═══════════════════════════════════════════════════════════════════
//  App.js
//  Dashboard · App
// ═══════════════════════════════════════════════════════════════════

import { useEffect, useState } from "react";
import { GlobalResponsiveStyles } from "./layout/GlobalResponsiveStyles";
import { MobileBottomNav } from "./layout/MobileBottomNav";
import { MobileDrawer } from "./layout/MobileDrawer";
import { MobileHeader } from "./layout/MobileHeader";
import { Sidebar } from "./layout/Sidebar";
import { Login } from "./modules/auth/Login";
import { ChatPage } from "./modules/chat/ChatPage";
import { Console } from "./modules/console/Console";
import { Courriel } from "./modules/courriel/Courriel";
import { DashboardHome } from "./modules/dashboard/DashboardHome";
import { Documents } from "./modules/documents/Documents";
import { ERP } from "./modules/erp/ERP";
import { ProjectsList } from "./modules/projets/ProjectsList";
import { Sanctions } from "./modules/sanctions/Sanctions";
import { Tickets } from "./modules/tickets/Tickets";
import { brancherWiki } from "./modules/wiki/navigation";
import { Wiki } from "./modules/wiki/Wiki";
import { decodeToken } from "./socle/api";
import { useIsMobile } from "./socle/hooks";
import { THEME } from "./socle/theme";

export function Dashboard({ user, token, onLogout }) {
  const [activeModule, setActiveModule] = useState("dashboard");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useIsMobile();

  // Lot 3 — navigation avec cible : « ouvre les Tickets SUR le ticket 42 ».
  // naviguer() remplace setActiveModule partout ci-dessous ; appelée sans
  // cible, elle efface la précédente (sinon un module la rouvrirait).
  const [cible, setCible] = useState(null);
  const naviguer = (module, c) => { setCible(c || null); setActiveModule(module); };
  useEffect(() => { brancherWiki(naviguer); }, []);

  const content = (
    <>
      {activeModule === "console" && <Console token={token} />}
      {activeModule === "tickets" && <Tickets token={token} cible={cible} onNavigate={naviguer} />}
      {activeModule === "erp" && <ERP token={token} user={user} cible={cible} onNavigate={naviguer} />}
      {activeModule === "kanban" && <ProjectsList token={token} currentUserId={user.userId} />}
      {activeModule === "docs" && <Documents token={token} user={user} />}
      {activeModule === "chat" && <ChatPage token={token} user={user} />}
      {activeModule === "wiki" && <Wiki token={token} user={user} onNavigate={naviguer} cible={cible} />}
      {activeModule === "dashboard" && <DashboardHome token={token} user={user} onNavigate={naviguer} />}
      {activeModule === "courriel" && <Courriel token={token} user={user} />}
      {activeModule === "sanctions" && <Sanctions token={token} user={user} onNavigate={naviguer} cible={cible} />}
    </>
  );

  if (isMobile) {
    return (
      <div style={{ minHeight: "100vh", background: THEME.bg, fontFamily: "Inter, -apple-system, sans-serif" }}>
        <MobileHeader onMenu={() => setDrawerOpen(true)} />
        <div style={{ padding: "14px 14px 84px", overflowX: "hidden" }}>{content}</div>
        <MobileBottomNav active={activeModule} role={user.role}
          onNavigate={(id) => { naviguer(id); setDrawerOpen(false); }}
          onMore={() => setDrawerOpen(true)} />
        <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}
          active={activeModule} onNavigate={naviguer} user={user} onLogout={onLogout} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: THEME.bg, display: "flex", fontFamily: "Inter, -apple-system, sans-serif" }}>
      <Sidebar active={activeModule} onNavigate={naviguer} user={user} onLogout={onLogout} />
      <div style={{ flex: 1, padding: "28px 32px", overflowY: "auto" }}>{content}</div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    if (urlToken) { localStorage.setItem("orialis_token", urlToken); window.history.replaceState({}, "", "/"); }
    const token = urlToken || localStorage.getItem("orialis_token");
    if (token) {
      const decoded = decodeToken(token);
      if (decoded && decoded.exp * 1000 > Date.now()) { setUser(decoded); }
      else { localStorage.removeItem("orialis_token"); }
    }
    setChecking(false);
  }, []);

  // PWA : enregistrement du service worker (installabilité, sans cache)
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").catch(() => {});
      });
    }
  }, []);

  const handleLogout = () => { localStorage.removeItem("orialis_token"); setUser(null); };

  if (checking) return null;
  if (!user) return (<><GlobalResponsiveStyles /><Login /></>);
  const token = localStorage.getItem("orialis_token");
  return (<><GlobalResponsiveStyles /><Dashboard user={user} token={token} onLogout={handleLogout} /></>);
}
